import axios from 'axios';
import {
  type RankingPopulated,
  type RankingAdminPopulated,
  type ConfirmScoreResponse,
  type CreateRankingRequest,
  type CreateRankingResponse,
  type MyRankingsResponse,
  type PaginatedRankingsResponse,
  type UnityScorePayload,
  type ConfirmScorePayload,
  type RankingQuotaResponse,
} from './rankings.service.types';
import { setupUserBlockInterceptor } from '@shared/utils/setupUserBlockInterceptor';
import { buildAntiBotMeta } from '@shared/utils/antiBot';
import { detectDeviceType } from '@shared/utils/device';

const rankingBack = axios.create({
  baseURL: import.meta.env.VITE_API_RANKING_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const rankingPublic = axios.create({
  baseURL: import.meta.env.VITE_API_RANKING_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

setupUserBlockInterceptor(rankingBack);

export const rankingService = {
  async getAllRankings(page = 1, limit = 20, signal?: AbortSignal) {
    return await rankingPublic.get<PaginatedRankingsResponse>('/rankings', {
      params: { page, limit },
      signal,
    });
  },

  async getAllRankingPrivate(page = 1, limit = 20, signal?: AbortSignal) {
    return await rankingBack.get<PaginatedRankingsResponse>(`/rankings/me`, {
      params: { page, limit },
      signal,
    });
  },

  async getRankingById(id: string) {
    return await rankingPublic.get<RankingPopulated>(`/rankings/${id}`);
  },

  async sendScore(payload: UnityScorePayload) {
    return await rankingBack.post<{ message: string }>(
      '/points/request-token',
      payload,
    );
  },

  async confirmScore(payload: ConfirmScorePayload, token: string) {
    const device_type = detectDeviceType();
    const meta = buildAntiBotMeta();
    const anti_bot_metrics = {
      uaInfo: meta.uaInfo,
      screen: meta.screen,
      system: meta.system,
      network: meta.network,
      signals: meta.signals,
    };
    const anti_bot_malicious = {
      keywords: meta.keywords,
      flags: meta.flags,
      riskScore: meta.riskScore,
      riskLevel: meta.riskLevel,
      reasons: meta.reasons,
    };

    const confirmPayload: ConfirmScorePayload = {
      ...payload,
      device_type: { type: device_type },
      anti_bot_metrics,
      anti_bot_malicious,
    };

    return await rankingBack.post<ConfirmScoreResponse>(
      '/points/request-token/confirm',
      confirmPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  async postChangePasswordRanking(
    newPassword: string,
    confirmPassword: string,
  ) {
    return await rankingBack.post('/change-password', {
      newPassword,
      confirmPassword,
    });
  },
  async putChangePasswordRanking(newPassword: string, confirmPassword: string) {
    return await rankingBack.put('/change-password', {
      newPassword,
      confirmPassword,
    });
  },

  async verifyPasswordRanking() {
    return await rankingBack.get<{ hasPassword: boolean }>('/verify-password');
  },

  // FASE 1: Session proxies required for Unity integration
  async proxyStartRankingSession(payload: Record<string, unknown>) {
    return await rankingBack.post<Record<string, unknown>>(
      '/session/start',
      payload,
    );
  },

  async proxyRankingSessionHeartbeat(payload: Record<string, unknown>) {
    return await rankingBack.post<Record<string, unknown>>(
      '/session/heartbeat',
      payload,
    );
  },

  async createRanking(data: CreateRankingRequest) {
    return await rankingBack.post<CreateRankingResponse>('/rankings', data);
  },

  async updateRanking(id: number, data: Partial<CreateRankingRequest>) {
    return await rankingBack.put<CreateRankingResponse>(
      `/rankings/${id.toString()}`,
      data,
    );
  },

  async getAdminRankingById(id: number) {
    return await rankingBack.get<RankingAdminPopulated>(
      `/rankings/${id.toString()}/owner`,
    );
  },

  async getMyPendingRankings() {
    return await rankingBack.get<MyRankingsResponse>('/rankings/me/pending');
  },

  async getMyRejectedRankings() {
    return await rankingBack.get<MyRankingsResponse>('/rankings/me/rejected');
  },

  async getMyActiveRankings() {
    return await rankingBack.get<MyRankingsResponse>('/rankings/me/active');
  },

  async getMyRankingQuota() {
    return await rankingBack.get<RankingQuotaResponse>('/rankings/me/quota');
  },
};
